"use client";

import { c as _c } from "react/compiler-runtime";
import { graphql, readFragment, useQuery } from "@opensea/graphql";
import { BottomSheetBody, BottomSheetHeader, BottomSheetHeaderTitle, Flex, FlexColumn, Separator, useToasts } from "@opensea/ui-kit";
import { useLocalStorage } from "@opensea/ui-kit/hooks";
import { useCallback, useMemo, useState } from "react";
import { useTranslations } from "use-intl";
import { useSnapshot } from "valtio";
import { MAX_PRICE_CHANGE_PERCENTAGE, PriceChangeError } from "../../../components/PriceChange";
import { usePriceValidation } from "../../../components/PriceChange/usePriceValidation";
import { buildPricingLookup } from "../../../components/QuoteReceipt";
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants";
import { useAddress } from "../../../polychain/hooks/useAddress";
import { FlowActions, FlowState } from "../../../stores/flowStore";
import { useAllActionsCompleted } from "../../hooks/useAllActionsCompleted";
import { useEndFlowOnAccountChange } from "../../hooks/useEndFlowOnAccountChange";
import { useSetItemImageMap, useSetItemImageMapFragment } from "../../hooks/useSetItemImageMap";
import { useSetQuoteItems, useSetQuoteItemsFragment } from "../../hooks/useSetQuoteItems";
import { useSetTotalUsdPrice, useSetTotalUsdPriceFragment } from "../../hooks/useSetTotalUsdPrice";
import { AcceptOfferToast } from "../../ToastHandler/AcceptOfferToast/AcceptOfferToast";
import { CREATOR_FEE_ENFORCED_LOCAL_STORAGE } from "../constants";
import { AcceptOfferFlow_quoteFragment } from "./AcceptOfferFlow.fragment";
import { AcceptOfferFlowSkeleton } from "./AcceptOfferFlowSkeleton";
import { AcceptOfferReceipt } from "./components/AcceptOfferReceipt";
import { AcceptOfferFlowStatus } from "./components/AcceptOfferStatus";
import { AcceptOffersTimeline } from "./components/AcceptOfferTimeline/AcceptOfferTimeline";
const query = graphql(`
    query AcceptOfferFlowQuery(
      $identifiers: [ItemQuantityInput!]!
      $address: Address
    ) {
      acceptOffersQuote(itemQuantities: $identifiers, address: $address) {
        ...AcceptOfferFlow_quote
        itemQuotes {
          ...useSetQuoteItems
          ...useSetItemImageMap
          ...useSetTotalUsdPrice
        }
      }
    }
  `, [AcceptOfferFlow_quoteFragment, useSetQuoteItemsFragment, useSetItemImageMapFragment, useSetTotalUsdPriceFragment]);
export function AcceptOfferFlow(t0) {
  const $ = _c(29);
  const {
    items: itemsWithOrder
  } = t0;
  const t = useTranslations("wallet.AcceptOfferFlow");
  const address = useAddress();
  const {
    addCustomToast
  } = useToasts();
  const [storedCreatorFeePreference, setStoredCreatorFeePreference] = useLocalStorage(CREATOR_FEE_ENFORCED_LOCAL_STORAGE, true);
  const [applyOptionalCreatorFees, setApplyOptionalCreatorFeesState] = useState(storedCreatorFeePreference);
  const {
    flow,
    itemQuotes
  } = useSnapshot(FlowState);
  const mode = flow?.mode;
  let t1;
  if ($[0] !== setStoredCreatorFeePreference) {
    t1 = value => {
      setApplyOptionalCreatorFeesState(value);
      setStoredCreatorFeePreference(value);
    };
    $[0] = setStoredCreatorFeePreference;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const setApplyOptionalCreatorFees = t1;
  let t2;
  let t3;
  if ($[2] !== itemsWithOrder) {
    t3 = buildPricingLookup(itemsWithOrder);
    $[2] = itemsWithOrder;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  t2 = t3;
  const initialPrices = t2;
  let t4;
  if ($[4] !== itemsWithOrder) {
    t4 = itemsWithOrder.map(_temp);
    $[4] = itemsWithOrder;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== address || $[7] !== t4) {
    t5 = {
      query,
      variables: {
        identifiers: t4,
        address
      },
      context: NETWORK_ONLY_OPERATION_CONTEXT
    };
    $[6] = address;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const [t6] = useQuery(t5);
  const {
    data,
    fetching
  } = t6;
  useSetQuoteItems(data?.acceptOffersQuote.itemQuotes);
  useSetItemImageMap(data?.acceptOffersQuote.itemQuotes);
  useSetTotalUsdPrice(data?.acceptOffersQuote.itemQuotes);
  let t7;
  if ($[9] !== itemQuotes || $[10] !== itemsWithOrder) {
    t7 = {
      itemsWithPricing: itemsWithOrder,
      flow: "accept-offer",
      itemQuotes
    };
    $[9] = itemQuotes;
    $[10] = itemsWithOrder;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const priceValidation = usePriceValidation(t7);
  useEndFlowOnAccountChange();
  const [allActionsCompleted] = useAllActionsCompleted();
  let t8;
  if ($[12] !== addCustomToast || $[13] !== mode) {
    t8 = (_response, receiptTransactions) => {
      if (mode === "minimized" && receiptTransactions.length > 0) {
        const {
          itemQuotes: itemQuotes_0,
          itemImageMap,
          flow: flow_0
        } = FlowState;
        addCustomToast(() => <AcceptOfferToast flow={flow_0} itemImageMap={itemImageMap} itemQuotes={itemQuotes_0} transactionIdentifiers={receiptTransactions} />, {
          id: "accept-offer-toast",
          duration: Number.POSITIVE_INFINITY
        });
        FlowActions.endFlow();
      }
    };
    $[12] = addCustomToast;
    $[13] = mode;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleAllActionsCompleted = t8;
  if (fetching) {
    let t9;
    if ($[15] !== itemsWithOrder.length) {
      t9 = <AcceptOfferFlowSkeleton count={itemsWithOrder.length} />;
      $[15] = itemsWithOrder.length;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    return t9;
  }
  if (!data?.acceptOffersQuote) {
    throw new Error("There was a problem with accepting offer.");
  }
  const t9 = data.acceptOffersQuote;
  let t10;
  let t11;
  if ($[17] !== allActionsCompleted || $[18] !== applyOptionalCreatorFees || $[19] !== data.acceptOffersQuote || $[20] !== handleAllActionsCompleted || $[21] !== initialPrices || $[22] !== priceValidation || $[23] !== setApplyOptionalCreatorFees || $[24] !== t) {
    t11 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const quote = readFragment(AcceptOfferFlow_quoteFragment, t9);
      const items = quote.itemQuotes.map(_temp2);
      if (!items.every(_temp3)) {
        t11 = null;
        break bb0;
      }
      if (priceValidation.changedItems.some(_temp4)) {
        let t12;
        if ($[27] !== priceValidation) {
          t12 = <PriceChangeError priceValidation={priceValidation} />;
          $[27] = priceValidation;
          $[28] = t12;
        } else {
          t12 = $[28];
        }
        t11 = t12;
        break bb0;
      }
      t10 = <>{allActionsCompleted ? <AcceptOfferFlowStatus /> : <><BottomSheetHeader><BottomSheetHeaderTitle>{t("accept", {
                count: items.length
              })}</BottomSheetHeaderTitle></BottomSheetHeader><BottomSheetBody className="mt-2 pb-2"><FlexColumn className="size-full"><AcceptOfferReceipt applyCreatorFee={applyOptionalCreatorFees} initialPrices={initialPrices} onCreatorFeeChange={setApplyOptionalCreatorFees} quote={quote} /><Separator /><Flex className="w-full py-4"><AcceptOffersTimeline applyOptionalCreatorFees={applyOptionalCreatorFees} onAllActionsCompleted={handleAllActionsCompleted} priceValidation={priceValidation} quote={quote} /></Flex></FlexColumn></BottomSheetBody></>}</>;
    }
    $[17] = allActionsCompleted;
    $[18] = applyOptionalCreatorFees;
    $[19] = data.acceptOffersQuote;
    $[20] = handleAllActionsCompleted;
    $[21] = initialPrices;
    $[22] = priceValidation;
    $[23] = setApplyOptionalCreatorFees;
    $[24] = t;
    $[25] = t10;
    $[26] = t11;
  } else {
    t10 = $[25];
    t11 = $[26];
  }
  if (t11 !== Symbol.for("react.early_return_sentinel")) {
    return t11;
  }
  return t10;
}
function _temp4(item_1) {
  return Math.abs(item_1.priceDifference) > item_1.initialPrice * MAX_PRICE_CHANGE_PERCENTAGE / 100;
}
function _temp3(item_0) {
  return item_0.__typename !== "ItemIdentifierResult";
}
function _temp2(itemQuote) {
  return itemQuote.item;
}
function _temp(item) {
  return {
    item: {
      tokenId: item.tokenId,
      contractAddress: item.contractAddress,
      chain: item.chain.identifier
    },
    quantity: item.quantity,
    orderId: item.orderId
  };
}